'use client';

import { Suspense } from 'react';

import { RealEstateDict, getLocalePrefix } from '@monoimin/intl';

import { managersFilterConfigs } from '../../configs/managers-filter-configs';
import { publicFilterConfigs } from '../../configs/public-filter-configs';

import { RenderFilterConfigs } from './RenderFilterConfigs';

import { RealEstateMainFilters } from './RealEstateMainFilters';
import { RealEstateMobileFilters } from './RealEstateMobileFilters';

import type { EnOrRuLocale } from '@monoimin/sb-types';

import type { FiltersData } from '../../types';

type RenderPageFiltersProps = FiltersData & {
  realEstateDict: RealEstateDict;
  containerClassName?: string;
  page: 'main' | 'list-static' | 'filter' | 'manager';
  locale: EnOrRuLocale;
  isPassportivity?: boolean;
};

export const RenderPageFilters = ({
  containerClassName,
  page,
  countries,
  cities,
  realEstateDict,
  objectTypes,
  developers,
  costForGreeceResponse,
  locale,
  isPassportivity,
}: RenderPageFiltersProps) => {
  const localePrefix = getLocalePrefix(locale);

  const isMainPage = page === 'main';

  const isListStaticPage = page === 'list-static';

  const isFilterPage = page === 'filter';

  const isManagerPage = page === 'manager';

  const renderPublicFilters = isListStaticPage || isFilterPage;

  const renderManagersFilters =
    isManagerPage && developers && costForGreeceResponse;

  const SuspenseFallback = <></>;

  return (
    <div className={containerClassName}>
      {isMainPage && (
        <Suspense>
          <RealEstateMainFilters
            countries={countries}
            cities={cities}
            className='max-lg:hidden'
            realEstateDict={realEstateDict}
          />
        </Suspense>
      )}
      {renderPublicFilters && (
        <Suspense fallback={SuspenseFallback}>
          <RenderFilterConfigs
            className='max-lg:hidden grid grid-cols-1 lg:grid-cols-5 gap-3'
            configs={publicFilterConfigs}
            countries={countries}
            cities={cities}
            realEstateDict={realEstateDict}
            objectTypes={objectTypes}
            newPathname={
              isListStaticPage
                ? isPassportivity
                  ? `${localePrefix}/search`
                  : `${localePrefix}/real-estate/search`
                : ''
            }
            locale={locale}
          />
        </Suspense>
      )}
      {renderManagersFilters && (
        <Suspense fallback={SuspenseFallback}>
          <RenderFilterConfigs
            className='max-lg:hidden grid grid-cols-1 lg:grid-cols-5 gap-3 pb-3'
            configs={managersFilterConfigs}
            countries={countries}
            cities={cities}
            realEstateDict={realEstateDict}
            objectTypes={objectTypes}
            developers={developers}
            costForGreeceResponse={costForGreeceResponse}
            locale={locale}
          />
        </Suspense>
      )}
      {!isManagerPage && (
        <Suspense fallback={SuspenseFallback}>
          <RealEstateMobileFilters
            className='lg:hidden'
            countries={countries}
            cities={cities}
            objectTypes={objectTypes}
            isMainPage={isMainPage}
            realEstateDict={realEstateDict}
          />
        </Suspense>
      )}
    </div>
  );
};
